import { useEffect, useRef, useState } from "react";

export const createPressRepeatController = (
  onRepeat,
  { delay = 400, interval = 120 } = {},
) => {
  let delayTimer = null;
  let intervalTimer = null;
  let repeated = false;

  const clearTimers = () => {
    if (delayTimer !== null) {
      clearTimeout(delayTimer);
      delayTimer = null;
    }
    if (intervalTimer !== null) {
      clearInterval(intervalTimer);
      intervalTimer = null;
    }
  };

  return {
    start() {
      clearTimers();
      repeated = false;
      delayTimer = setTimeout(() => {
        delayTimer = null;
        repeated = true;
        onRepeat();
        intervalTimer = setInterval(onRepeat, interval);
      }, delay);
    },
    stop() {
      clearTimers();
    },
    cancel() {
      clearTimers();
      repeated = false;
    },
    consumeClick() {
      const shouldActivate = !repeated;
      repeated = false;
      return shouldActivate;
    },
  };
};

export const usePressRepeat = (
  onActivate,
  { delay = 400, interval = 120 } = {},
) => {
  const callbackRef = useRef(onActivate);
  const controllerRef = useRef(null);
  const [isPressed, setIsPressed] = useState(false);

  callbackRef.current = onActivate;
  if (controllerRef.current === null) {
    controllerRef.current = createPressRepeatController(
      () => callbackRef.current(),
      { delay, interval },
    );
  }

  useEffect(
    () => () => {
      controllerRef.current?.cancel();
    },
    [],
  );

  const stopPress = () => {
    controllerRef.current.stop();
    setIsPressed(false);
  };

  const cancelPress = () => {
    controllerRef.current.cancel();
    setIsPressed(false);
  };

  return {
    isPressed,
    pressProps: {
      onPointerDown: (event) => {
        if (event.button !== 0) return;
        event.currentTarget.setPointerCapture?.(event.pointerId);
        setIsPressed(true);
        controllerRef.current.start();
      },
      onPointerUp: stopPress,
      onPointerLeave: cancelPress,
      onPointerCancel: cancelPress,
      onBlur: cancelPress,
      onClick: () => {
        if (controllerRef.current.consumeClick()) {
          callbackRef.current();
        }
      },
      onContextMenu: (event) => {
        if (isPressed) event.preventDefault();
      },
    },
  };
};
